// frontend/src/pages/CalendarPage.js
// Phase I: カレンダー機能リバイバル（Supabase 完全新規版）
//
// 機能:
//   - 月ビュー: 自分のシフト + 個人イベント + 祝日を表示
//   - 日詳細: クリックで右パネルに自分の予定 + 共有接続スタッフのシフトを表示
//   - 接続管理: 「このスタッフのシフトを見たい」を追加/削除
//   - 個人イベント: 私用予定の追加/編集/削除

import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { supabase } from "../supabase";
import { useOrganization } from "../contexts/OrganizationContext";
import Header from "../components/ui/Header";
import {
  ChevronLeft,
  ChevronRight,
  Users,
  Plus,
  X,
  Search,
  Trash2,
  Calendar as CalendarIcon,
} from "lucide-react";

const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"];
const MOBILE_BREAKPOINT = 768;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
};

const CalendarPage = () => {
  const { currentOrganization } = useOrganization();
  const isMobile = useIsMobile();
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [myStaff, setMyStaff] = useState(null);
  // モバイル時、日付タップで詳細をボトムシート表示するかどうか
  const [showDayDetailSheet, setShowDayDetailSheet] = useState(false);

  // monthly_shifts キャッシュ: { [staffId]: { [date]: shift_type } }
  const [shiftsByStaff, setShiftsByStaff] = useState({});
  // 接続スタッフ一覧
  const [connections, setConnections] = useState([]);
  // 個人イベント: 配列
  const [personalEvents, setPersonalEvents] = useState([]);
  // 祝日
  const [holidays, setHolidays] = useState([]);
  // 全スタッフ（接続追加用）
  const [allStaff, setAllStaff] = useState([]);
  // 自分の提出済みシフト希望: { [YYYY-MM-DD]: { selection, comment } }
  const [myHopesByDate, setMyHopesByDate] = useState({});

  // モーダル状態
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);
  const [showEventEditor, setShowEventEditor] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  // 自分の staff 情報を localStorage から取得
  useEffect(() => {
    try {
      const raw = localStorage.getItem("supabase_user");
      if (raw) {
        const u = JSON.parse(raw);
        setMyStaff({
          id: u?.id,
          employeeId: u?.employeeId || u?.employee_id,
          name: u?.name,
        });
      }
    } catch (_) { /* ignore */ }
  }, []);

  const orgId = currentOrganization?.id;
  const myStaffId = myStaff?.id;

  // 月の日付配列
  const monthDates = useMemo(() => {
    const first = currentMonth.startOf("month");
    const daysInMonth = currentMonth.daysInMonth();
    return Array.from({ length: daysInMonth }, (_, i) =>
      first.add(i, "day").format("YYYY-MM-DD")
    );
  }, [currentMonth]);

  // カレンダーグリッド用：月初の曜日に合わせて先頭に空セル
  const gridCells = useMemo(() => {
    const first = currentMonth.startOf("month");
    const startOffset = first.day();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    monthDates.forEach((d) => cells.push(d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [currentMonth, monthDates]);

  // 月変化時に各データを取得
  useEffect(() => {
    if (!orgId || !myStaffId) return;
    const first = monthDates[0];
    const last = monthDates[monthDates.length - 1];
    const myEmpId = myStaff?.employeeId;
    if (!first || !last) return;

    const loadAll = async () => {
      // 自分 + 接続先のシフトをまとめて取得
      const targetEmpIds = new Set();
      if (myEmpId) targetEmpIds.add(String(myEmpId));
      // 接続先の staff.id → employee_id を解決するため staff テーブル参照
      const { data: connData } = await supabase
        .from("shift_share_connections")
        .select("*")
        .eq("organization_id", orgId)
        .eq("viewer_staff_id", myStaffId);
      const conns = connData || [];

      const targetStaffIds = conns.map((c) => c.target_staff_id);
      let staffMeta = [];
      if (targetStaffIds.length > 0) {
        const { data: stData } = await supabase
          .from("staff")
          .select("id, employee_id, last_name, first_name")
          .eq("organization_id", orgId)
          .in("id", targetStaffIds);
        staffMeta = stData || [];
      }
      const idToEmp = {};
      const idToName = {};
      staffMeta.forEach((s) => {
        idToEmp[s.id] = s.employee_id;
        idToName[s.id] = `${s.last_name || ""} ${s.first_name || ""}`.trim();
        if (s.employee_id) targetEmpIds.add(String(s.employee_id));
      });

      const enriched = conns.map((c) => ({
        ...c,
        employeeId: idToEmp[c.target_staff_id] || null,
        nameFromDb: idToName[c.target_staff_id] || c.target_staff_id,
      }));
      setConnections(enriched);

      // monthly_shifts まとめて取得 (公開済みの月だけ)。
      // 公開チェック: shift_publications に (org_id, year, month) 行があるかを確認。
      // 読み取りに失敗した場合も「未公開扱い」で隠す (= 公開前の情報が漏れない)。
      const empIdsArr = Array.from(targetEmpIds);
      const monthYear = currentMonth.year();
      const monthNum = currentMonth.month() + 1;
      const { data: pubRows, error: pubErr } = await supabase
        .from("shift_publications")
        .select("published_at")
        .eq("organization_id", orgId)
        .eq("year", monthYear)
        .eq("month", monthNum)
        .limit(1);
      if (pubErr) {
        console.warn("[CalendarPage] shift_publications 取得エラー:", pubErr);
      }
      const isMonthPublished = !pubErr && Array.isArray(pubRows) && pubRows.length > 0;
      console.log("[CalendarPage] 公開判定:", { orgId, year: monthYear, month: monthNum, pubRows, isMonthPublished });

      if (empIdsArr.length > 0 && isMonthPublished) {
        const { data: shiftData } = await supabase
          .from("monthly_shifts")
          .select("employee_id, shift_date, shift_type")
          .eq("organization_id", orgId)
          .in("employee_id", empIdsArr)
          .gte("shift_date", first)
          .lte("shift_date", last);
        const map = {};
        (shiftData || []).forEach((row) => {
          const emp = String(row.employee_id);
          if (!map[emp]) map[emp] = {};
          map[emp][row.shift_date] = row.shift_type || "";
        });
        setShiftsByStaff(map);
      } else {
        // 未公開月はシフト非表示
        setShiftsByStaff({});
      }

      // 個人イベント
      const { data: events } = await supabase
        .from("personal_calendar_events")
        .select("*")
        .eq("organization_id", orgId)
        .eq("owner_staff_id", myStaffId)
        .gte("event_date", first)
        .lte("event_date", last)
        .order("event_date", { ascending: true });
      setPersonalEvents(events || []);

      // 祝日
      const { data: hols } = await supabase
        .from("holidays")
        .select("date")
        .eq("organization_id", orgId)
        .gte("date", first)
        .lte("date", last);
      setHolidays((hols || []).map((h) => h.date));

      // 自分のシフト希望 (shift_requests) を取得
      const myEmpId = myStaff?.employeeId;
      if (myEmpId) {
        const { data: hopes } = await supabase
          .from("shift_requests")
          .select("shift_date, selection, comment")
          .eq("organization_id", orgId)
          .eq("employee_id", myEmpId)
          .gte("shift_date", first)
          .lte("shift_date", last);
        const hopeMap = {};
        (hopes || []).forEach((h) => {
          hopeMap[h.shift_date] = { selection: h.selection, comment: h.comment };
        });
        setMyHopesByDate(hopeMap);
      } else {
        setMyHopesByDate({});
      }
    };
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, myStaffId, currentMonth.format("YYYY-MM")]);

  // 全スタッフ取得（接続追加モーダル用）— 必要になったときだけ
  const loadAllStaff = async () => {
    if (!orgId || allStaff.length > 0) return;
    const { data } = await supabase
      .from("staff")
      .select("id, employee_id, last_name, first_name")
      .eq("organization_id", orgId)
      .eq("is_active", true)
      .order("employee_id", { ascending: true });
    setAllStaff(data || []);
  };

  const myShifts = myStaff?.employeeId
    ? shiftsByStaff[String(myStaff.employeeId)] || {}
    : {};

  const eventsByDate = useMemo(() => {
    const map = {};
    personalEvents.forEach((e) => {
      if (!map[e.event_date]) map[e.event_date] = [];
      map[e.event_date].push(e);
    });
    return map;
  }, [personalEvents]);

  const refreshEvents = async () => {
    if (!orgId || !myStaffId) return;
    const first = monthDates[0];
    const last = monthDates[monthDates.length - 1];
    const { data: events } = await supabase
      .from("personal_calendar_events")
      .select("*")
      .eq("organization_id", orgId)
      .eq("owner_staff_id", myStaffId)
      .gte("event_date", first)
      .lte("event_date", last)
      .order("event_date", { ascending: true });
    setPersonalEvents(events || []);
  };

  const refreshConnections = async () => {
    if (!orgId || !myStaffId) return;
    const { data: connData } = await supabase
      .from("shift_share_connections")
      .select("*")
      .eq("organization_id", orgId)
      .eq("viewer_staff_id", myStaffId);
    const conns = connData || [];
    const targetStaffIds = conns.map((c) => c.target_staff_id);
    let staffMeta = [];
    if (targetStaffIds.length > 0) {
      const { data: stData } = await supabase
        .from("staff")
        .select("id, employee_id, last_name, first_name")
        .eq("organization_id", orgId)
        .in("id", targetStaffIds);
      staffMeta = stData || [];
    }
    const idToEmp = {};
    const idToName = {};
    staffMeta.forEach((s) => {
      idToEmp[s.id] = s.employee_id;
      idToName[s.id] = `${s.last_name || ""} ${s.first_name || ""}`.trim();
    });
    setConnections(
      conns.map((c) => ({
        ...c,
        employeeId: idToEmp[c.target_staff_id] || null,
        nameFromDb: idToName[c.target_staff_id] || c.target_staff_id,
      }))
    );
    // 接続変更後はシフトも更新
    const empIds = new Set();
    if (myStaff?.employeeId) empIds.add(String(myStaff.employeeId));
    conns.forEach((c) => {
      const emp = idToEmp[c.target_staff_id];
      if (emp) empIds.add(String(emp));
    });
    const empArr = Array.from(empIds);
    if (empArr.length > 0) {
      const { data: shiftData } = await supabase
        .from("monthly_shifts")
        .select("employee_id, shift_date, shift_type")
        .eq("organization_id", orgId)
        .in("employee_id", empArr)
        .gte("shift_date", monthDates[0])
        .lte("shift_date", monthDates[monthDates.length - 1]);
      const map = {};
      (shiftData || []).forEach((row) => {
        const emp = String(row.employee_id);
        if (!map[emp]) map[emp] = {};
        map[emp][row.shift_date] = row.shift_type || "";
      });
      setShiftsByStaff(map);
    }
  };

  // 日のセルカラー（土日・祝日）
  const cellColor = (date) => {
    if (!date) return null;
    const d = dayjs(date).day();
    const isHol = holidays.includes(date);
    if (d === 0 || isHol) return { bg: "#fef2f2", text: "#b91c1c" };
    if (d === 6) return { bg: "#eff6ff", text: "#1d4ed8" };
    return { bg: "#ffffff", text: "#1f2937" };
  };

  return (
    <div style={styles.page}>
      <Header />
      <div style={{ ...styles.container, padding: isMobile ? "0.75rem 0.5rem" : "1.5rem 1rem" }}>
        {/* ヘッダー：月ナビ */}
        <div
          style={{
            ...styles.monthBar,
            flexWrap: isMobile ? "wrap" : "nowrap",
            padding: isMobile ? "10px 12px" : "12px 16px",
            gap: isMobile ? 8 : 12,
          }}
        >
          <button
            onClick={() => setCurrentMonth(currentMonth.subtract(1, "month"))}
            style={styles.navBtn}
          >
            <ChevronLeft size={isMobile ? 18 : 20} />
          </button>
          <h1
            style={{
              ...styles.monthTitle,
              fontSize: isMobile ? "1.05rem" : "1.3rem",
              minWidth: isMobile ? 110 : 140,
            }}
          >
            {currentMonth.format("YYYY年 M月")}
          </h1>
          <button
            onClick={() => setCurrentMonth(currentMonth.add(1, "month"))}
            style={styles.navBtn}
          >
            <ChevronRight size={isMobile ? 18 : 20} />
          </button>
          <button
            onClick={() => setCurrentMonth(dayjs())}
            style={{
              ...styles.todayBtn,
              padding: isMobile ? "4px 10px" : "6px 14px",
            }}
          >
            今日
          </button>
          {!isMobile && <div style={{ flex: 1 }} />}
          <button
            onClick={() => {
              loadAllStaff();
              setShowConnectionsModal(true);
            }}
            style={{
              ...styles.actionBtn,
              marginLeft: isMobile ? "auto" : 0,
              padding: isMobile ? "6px 12px" : "8px 16px",
              fontSize: isMobile ? "0.8rem" : "0.9rem",
            }}
          >
            <Users size={isMobile ? 14 : 16} />
            {isMobile ? "共有" : "シフトを共有"}
            {connections.length > 0 && (
              <span style={styles.badge}>{connections.length}</span>
            )}
          </button>
        </div>

        <div
          style={{
            ...styles.bodyGrid,
            gridTemplateColumns: isMobile ? "1fr" : "1fr 360px",
            gap: isMobile ? 12 : 16,
          }}
        >
          {/* 左：月カレンダー */}
          <div style={{ ...styles.calendarCard, padding: isMobile ? 10 : 16 }}>
            <div style={styles.weekHeader}>
              {WEEKDAY_LABELS.map((w, i) => (
                <div
                  key={w}
                  style={{
                    ...styles.weekCell,
                    color: i === 0 ? "#dc2626" : i === 6 ? "#2563eb" : "#374151",
                    fontSize: isMobile ? "0.75rem" : "0.85rem",
                    padding: isMobile ? "4px 0" : "8px 0",
                  }}
                >
                  {w}
                </div>
              ))}
            </div>
            <div style={{ ...styles.dayGrid, gap: isMobile ? 2 : 4 }}>
              {gridCells.map((date, idx) => {
                if (!date) {
                  return (
                    <div
                      key={`empty-${idx}`}
                      style={{
                        ...styles.dayCellEmpty,
                        minHeight: isMobile ? 60 : 90,
                      }}
                    />
                  );
                }
                const isSelected = date === selectedDate;
                const isToday = date === dayjs().format("YYYY-MM-DD");
                const color = cellColor(date);
                const myMark = myShifts[date] || "";
                const myHope = myHopesByDate[date];
                const evs = eventsByDate[date] || [];
                return (
                  <button
                    key={date}
                    onClick={() => {
                      setSelectedDate(date);
                      if (isMobile) setShowDayDetailSheet(true);
                    }}
                    style={{
                      ...styles.dayCell,
                      // 希望提出済みは背景を薄い黄色でハイライト
                      background: myHope ? "#fef9c3" : color.bg,
                      border: isSelected
                        ? "2px solid #0891b2"
                        : myHope
                          ? "2px solid #f59e0b"
                          : "1px solid #e5e7eb",
                      boxShadow: isToday ? "inset 0 0 0 2px #fbbf24" : "none",
                      minHeight: isMobile ? 60 : 90,
                      padding: isMobile ? 3 : 6,
                      borderRadius: isMobile ? 6 : 8,
                      position: "relative",
                    }}
                    title={myHope ? `希望提出済: ${myHope.selection || ""}` : undefined}
                  >
                    <div
                      style={{
                        ...styles.dayNum,
                        color: color.text,
                        fontSize: isMobile ? "0.8rem" : "1rem",
                      }}
                    >
                      {dayjs(date).date()}
                    </div>
                    {myHope && (
                      <div style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        background: "#f59e0b",
                        color: "#fff",
                        fontSize: isMobile ? "0.55rem" : "0.65rem",
                        padding: "1px 4px",
                        borderRadius: 4,
                        fontWeight: 700,
                        lineHeight: 1.1,
                      }}>
                        希望
                      </div>
                    )}
                    {myMark && (
                      <div
                        style={{
                          ...styles.shiftMark,
                          marginTop: isMobile ? 2 : 6,
                          fontSize: isMobile ? "0.7rem" : "1rem",
                          padding: isMobile ? "1px 0" : "2px 0",
                        }}
                      >
                        {myMark}
                      </div>
                    )}
                    {evs.length > 0 && (
                      <div style={styles.eventDots}>
                        {evs.slice(0, 3).map((e) => (
                          <span
                            key={e.id}
                            style={{
                              ...styles.eventDot,
                              background: e.color || "#3b82f6",
                              width: isMobile ? 4 : 6,
                              height: isMobile ? 4 : 6,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 右：日詳細（デスクトップ時のみインライン表示） */}
          {!isMobile && (
            <div style={styles.detailCard}>
              <DayDetailPanel
                date={selectedDate}
                myShift={myShifts[selectedDate]}
                myEvents={eventsByDate[selectedDate] || []}
                connections={connections}
                shiftsByStaff={shiftsByStaff}
                onAddEvent={() => {
                  setEditingEvent({ event_date: selectedDate, title: "", note: "", color: "#3b82f6" });
                  setShowEventEditor(true);
                }}
                onEditEvent={(e) => {
                  setEditingEvent(e);
                  setShowEventEditor(true);
                }}
                onRequestRefresh={refreshEvents}
              />
            </div>
          )}
        </div>

        {/* 共有シフト一覧（接続スタッフがいる時のみ） */}
        {connections.length > 0 && (
          <SharedShiftMatrix
            isMobile={isMobile}
            monthDates={monthDates}
            holidays={holidays}
            myStaff={myStaff}
            myShifts={myShifts}
            connections={connections}
            shiftsByStaff={shiftsByStaff}
            selectedDate={selectedDate}
            onSelectDate={(d) => {
              setSelectedDate(d);
              if (isMobile) setShowDayDetailSheet(true);
            }}
            cellColor={cellColor}
          />
        )}
      </div>

      {/* モバイル：日詳細ボトムシート */}
      {isMobile && showDayDetailSheet && (
        <div
          style={{
            ...styles.overlay,
            alignItems: "flex-end",
            padding: 0,
          }}
          onClick={() => setShowDayDetailSheet(false)}
        >
          <div
            style={styles.bottomSheet}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.sheetGrabber} />
            <button
              onClick={() => setShowDayDetailSheet(false)}
              style={styles.sheetCloseBtn}
              aria-label="閉じる"
            >
              <X size={20} />
            </button>
            <div style={{ padding: "8px 16px 24px" }}>
              <DayDetailPanel
                date={selectedDate}
                myShift={myShifts[selectedDate]}
                myEvents={eventsByDate[selectedDate] || []}
                connections={connections}
                shiftsByStaff={shiftsByStaff}
                onAddEvent={() => {
                  setEditingEvent({ event_date: selectedDate, title: "", note: "", color: "#3b82f6" });
                  setShowEventEditor(true);
                }}
                onEditEvent={(e) => {
                  setEditingEvent(e);
                  setShowEventEditor(true);
                }}
                onRequestRefresh={refreshEvents}
              />
            </div>
          </div>
        </div>
      )}

      {/* 接続管理モーダル */}
      {showConnectionsModal && (
        <ConnectionsModal
          orgId={orgId}
          myStaffId={myStaffId}
          allStaff={allStaff}
          connections={connections}
          onClose={() => setShowConnectionsModal(false)}
          onChange={refreshConnections}
        />
      )}

      {/* イベント編集モーダル */}
      {showEventEditor && editingEvent && (
        <EventEditorModal
          orgId={orgId}
          myStaffId={myStaffId}
          event={editingEvent}
          onClose={() => {
            setShowEventEditor(false);
            setEditingEvent(null);
          }}
          onSaved={() => {
            refreshEvents();
            setShowEventEditor(false);
            setEditingEvent(null);
          }}
        />
      )}
    </div>
  );
};

// =============================================================================
// 日詳細パネル
// =============================================================================
const DayDetailPanel = ({
  date,
  myShift,
  myEvents,
  connections,
  shiftsByStaff,
  onAddEvent,
  onEditEvent,
  onRequestRefresh,
}) => {
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("この予定を削除しますか？")) return;
    try {
      const { error } = await supabase
        .from("personal_calendar_events")
        .delete()
        .eq("id", eventId);
      if (error) throw error;
      onRequestRefresh?.();
    } catch (e) {
      alert("削除に失敗しました: " + e.message);
    }
  };

  return (
    <div>
      <div style={styles.detailHeader}>
        <CalendarIcon size={20} color="#0891b2" />
        <h2 style={styles.detailTitle}>{dayjs(date).format("M月D日 (ddd)")}</h2>
      </div>

      {/* 自分のシフト */}
      <div style={styles.detailSection}>
        <div style={styles.sectionLabel}>自分のシフト</div>
        <div style={styles.myShiftBox}>
          {myShift ? (
            <span style={styles.myShiftMark}>{myShift}</span>
          ) : (
            <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
              シフト未登録
            </span>
          )}
        </div>
      </div>

      {/* 自分の予定 */}
      <div style={styles.detailSection}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={styles.sectionLabel}>自分の予定</div>
          <button onClick={onAddEvent} style={styles.miniAddBtn}>
            <Plus size={12} />
            追加
          </button>
        </div>
        {myEvents.length === 0 ? (
          <div style={{ color: "#94a3b8", fontStyle: "italic", padding: "0.5rem 0" }}>
            予定なし
          </div>
        ) : (
          myEvents.map((e) => (
            <div key={e.id} style={styles.eventItem}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: e.color || "#3b82f6",
                  marginRight: 8,
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: "#1f2937" }}>
                  {e.title}
                </div>
                {e.note && (
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {e.note}
                  </div>
                )}
              </div>
              <button
                onClick={() => onEditEvent(e)}
                style={styles.iconBtnSmall}
                title="編集"
              >
                ✏️
              </button>
              <button
                onClick={() => handleDeleteEvent(e.id)}
                style={styles.iconBtnSmall}
                title="削除"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))
        )}
      </div>

      {/* 共有スタッフのシフト */}
      <div style={styles.detailSection}>
        <div style={styles.sectionLabel}>
          共有スタッフのシフト ({connections.length}名)
        </div>
        {connections.length === 0 ? (
          <div style={{ color: "#94a3b8", fontStyle: "italic", padding: "0.5rem 0" }}>
            まだシフトを共有していません。上の「シフトを共有」から追加してください。
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {connections.map((c) => {
              const empId = c.employeeId ? String(c.employeeId) : null;
              const shift = empId ? shiftsByStaff[empId]?.[date] : null;
              const name = c.display_name || c.nameFromDb || c.target_staff_id;
              return (
                <div key={c.id} style={styles.connRow}>
                  <div style={{ flex: 1, fontSize: 14, color: "#1f2937" }}>
                    {name}
                  </div>
                  <div style={styles.connShiftMark}>
                    {shift || "—"}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// 接続管理モーダル
// =============================================================================
const ConnectionsModal = ({ orgId, myStaffId, allStaff, connections, onClose, onChange }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const connectedIds = new Set(connections.map((c) => c.target_staff_id));

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allStaff
      .filter((s) => s.id !== myStaffId)
      .filter((s) => {
        if (!q) return true;
        const name = `${s.last_name || ""} ${s.first_name || ""}`.toLowerCase();
        return name.includes(q) || (s.employee_id || "").toLowerCase().includes(q);
      });
  }, [allStaff, searchQuery, myStaffId]);

  const handleAdd = async (targetStaffId) => {
    try {
      const { error } = await supabase
        .from("shift_share_connections")
        .insert({
          organization_id: orgId,
          viewer_staff_id: myStaffId,
          target_staff_id: targetStaffId,
        });
      if (error) throw error;
      onChange?.();
    } catch (e) {
      alert("追加に失敗しました: " + e.message);
    }
  };

  const handleRemove = async (connectionId) => {
    if (!window.confirm("この共有を解除しますか？")) return;
    try {
      const { error } = await supabase
        .from("shift_share_connections")
        .delete()
        .eq("id", connectionId);
      if (error) throw error;
      onChange?.();
    } catch (e) {
      alert("削除に失敗しました: " + e.message);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            <Users size={20} style={{ verticalAlign: "middle", marginRight: 6 }} />
            シフト共有の管理
          </h2>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={20} />
          </button>
        </div>
        <div style={styles.modalBody}>
          <p style={{ fontSize: 13, color: "#475569", marginTop: 0 }}>
            選択したスタッフのシフトを、自分のカレンダーで閲覧できます。
            <br />
            （追加した時点で見える設定。相手側に通知は飛びません）
          </p>
          <div style={styles.searchBox}>
            <Search size={16} color="#94a3b8" />
            <input
              type="text"
              placeholder="名前 / 社員番号で検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <div style={styles.staffList}>
            {filtered.map((s) => {
              const isConnected = connectedIds.has(s.id);
              const connection = connections.find((c) => c.target_staff_id === s.id);
              return (
                <div
                  key={s.id}
                  style={{
                    ...styles.staffRow,
                    background: isConnected ? "#ecfeff" : "white",
                    borderColor: isConnected ? "#67e8f9" : "#e5e7eb",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>
                      {s.last_name} {s.first_name}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      社員番号 {s.employee_id}
                    </div>
                  </div>
                  {isConnected ? (
                    <button
                      onClick={() => handleRemove(connection.id)}
                      style={styles.removeBtn}
                    >
                      解除
                    </button>
                  ) : (
                    <button
                      onClick={() => handleAdd(s.id)}
                      style={styles.addBtn}
                    >
                      <Plus size={14} />
                      追加
                    </button>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ padding: "1rem", textAlign: "center", color: "#94a3b8" }}>
                該当するスタッフがいません
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// 個人イベント編集モーダル
// =============================================================================
const EventEditorModal = ({ orgId, myStaffId, event, onClose, onSaved }) => {
  const [form, setForm] = useState({
    event_date: event?.event_date || dayjs().format("YYYY-MM-DD"),
    title: event?.title || "",
    note: event?.note || "",
    color: event?.color || "#3b82f6",
  });
  const [saving, setSaving] = useState(false);

  const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

  const handleSave = async () => {
    if (!form.title.trim()) {
      alert("タイトルを入力してください");
      return;
    }
    setSaving(true);
    try {
      if (event?.id) {
        const { error } = await supabase
          .from("personal_calendar_events")
          .update({
            event_date: form.event_date,
            title: form.title.trim(),
            note: form.note || null,
            color: form.color,
            updated_at: new Date().toISOString(),
          })
          .eq("id", event.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("personal_calendar_events")
          .insert({
            organization_id: orgId,
            owner_staff_id: myStaffId,
            event_date: form.event_date,
            title: form.title.trim(),
            note: form.note || null,
            color: form.color,
          });
        if (error) throw error;
      }
      onSaved?.();
    } catch (e) {
      alert("保存に失敗しました: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            {event?.id ? "予定を編集" : "予定を追加"}
          </h2>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={20} />
          </button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.formGroup}>
            <label style={styles.label}>日付</label>
            <input
              type="date"
              value={form.event_date}
              onChange={(e) => setForm({ ...form, event_date: e.target.value })}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>タイトル *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="例: 通院、家族旅行、研修"
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>メモ（任意）</label>
            <textarea
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={3}
              style={{ ...styles.input, resize: "vertical" }}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>色</label>
            <div style={{ display: "flex", gap: 8 }}>
              {colors.map((c) => (
                <button
                  key={c}
                  onClick={() => setForm({ ...form, color: c })}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: c,
                    border: form.color === c ? "3px solid #1f2937" : "1px solid #e5e7eb",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.cancelBtn}>
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...styles.primaryBtn,
              opacity: saving ? 0.6 : 1,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// 共有シフトマトリクス（月分の比較表）
// =============================================================================
//   行 = 日付（その月の全日）
//   列 = 自分 + 接続スタッフ
//   セル = その日のシフトマーク（休/夜などにそれっぽい色付け）
//   選択中の日と「自分・全員が休み」の日はハイライト
// =============================================================================
const REST_MARKS = new Set(["休", "公", "公休", "有", "有休", "年", "年休", "代", "代休"]);

const markCellStyle = (mark) => {
  if (!mark) return { background: "transparent", color: "#cbd5e1" };
  if (REST_MARKS.has(mark)) return { background: "#fef3c7", color: "#b45309" };
  if (mark.includes("夜") || mark === "Ｎ" || mark === "N") return { background: "#312e81", color: "#fff" };
  if (mark === "明" || mark === "明け") return { background: "#e9d5ff", color: "#6b21a8" };
  if (mark.includes("日")) return { background: "#dbeafe", color: "#1e40af" };
  return { background: "#f1f5f9", color: "#1f2937" };
};

const SharedShiftMatrix = ({
  isMobile,
  monthDates,
  holidays,
  myStaff,
  myShifts,
  connections,
  shiftsByStaff,
  selectedDate,
  onSelectDate,
  cellColor,
}) => {
  // 列定義: 自分 + 接続スタッフ
  const columns = useMemo(() => {
    const cols = [
      {
        key: "me",
        label: myStaff?.name || "自分",
        getShift: (date) => myShifts[date] || "",
        isMe: true,
      },
    ];
    connections.forEach((c) => {
      const empId = c.employeeId ? String(c.employeeId) : null;
      cols.push({
        key: c.id,
        label: c.display_name || c.nameFromDb || c.target_staff_id,
        getShift: (date) => (empId ? shiftsByStaff[empId]?.[date] || "" : ""),
      });
    });
    return cols;
  }, [myStaff, myShifts, connections, shiftsByStaff]);

  // 全員が休みの日を「予定合わせやすい日」としてハイライト
  const matchDays = useMemo(() => {
    const set = new Set();
    monthDates.forEach((d) => {
      const allRest = columns.every((col) => {
        const m = col.getShift(d);
        return m && REST_MARKS.has(m);
      });
      if (allRest) set.add(d);
    });
    return set;
  }, [monthDates, columns]);

  return (
    <div
      style={{
        background: "white",
        borderRadius: 12,
        padding: isMobile ? 10 : 16,
        marginTop: isMobile ? 12 : 16,
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
          flexWrap: "wrap",
          gap: 6,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: isMobile ? "0.95rem" : "1.05rem",
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          共有シフト一覧（{columns.length}名）
        </h3>
        <div
          style={{
            display: "flex",
            gap: 8,
            fontSize: "0.7rem",
            color: "#475569",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 10, background: "#fef3c7", borderRadius: 2, display: "inline-block" }} />
            休
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 10, background: "#dbeafe", borderRadius: 2, display: "inline-block" }} />
            日勤
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 10, background: "#312e81", borderRadius: 2, display: "inline-block" }} />
            夜勤
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 10, background: "#e9d5ff", borderRadius: 2, display: "inline-block" }} />
            明け
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 10, background: "#dcfce7", borderRadius: 2, display: "inline-block", border: "1px solid #86efac" }} />
            全員休
          </span>
        </div>
      </div>

      <div
        style={{
          overflowX: "auto",
          maxHeight: isMobile ? 460 : 600,
          overflowY: "auto",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
        }}
      >
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "100%",
            minWidth: 80 + columns.length * 64,
            fontSize: isMobile ? "0.75rem" : "0.85rem",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  position: "sticky",
                  top: 0,
                  left: 0,
                  zIndex: 3,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                  borderRight: "1px solid #e2e8f0",
                  padding: "6px 8px",
                  textAlign: "left",
                  fontWeight: 700,
                  color: "#475569",
                  minWidth: 76,
                }}
              >
                日付
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    background: col.isMe ? "#ecfeff" : "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    borderRight: "1px solid #e2e8f0",
                    padding: "6px 8px",
                    fontWeight: 700,
                    color: col.isMe ? "#0e7490" : "#1f2937",
                    minWidth: 60,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                  }}
                  title={col.label}
                >
                  {col.isMe ? "👤 自分" : col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthDates.map((date) => {
              const d = dayjs(date);
              const color = cellColor(date);
              const isSelected = date === selectedDate;
              const isMatch = matchDays.has(date);
              return (
                <tr
                  key={date}
                  onClick={() => onSelectDate?.(date)}
                  style={{
                    background: isSelected
                      ? "#cffafe"
                      : isMatch
                      ? "#dcfce7"
                      : color.bg,
                    cursor: "pointer",
                  }}
                >
                  <td
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      background: isSelected
                        ? "#cffafe"
                        : isMatch
                        ? "#dcfce7"
                        : color.bg,
                      borderBottom: "1px solid #e2e8f0",
                      borderRight: "1px solid #e2e8f0",
                      padding: "5px 8px",
                      fontWeight: 600,
                      color: color.text,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {d.format("M/D")} ({WEEKDAY_LABELS[d.day()]})
                    {holidays.includes(date) && (
                      <span style={{ marginLeft: 4, color: "#dc2626", fontSize: "0.65rem" }}>
                        祝
                      </span>
                    )}
                  </td>
                  {columns.map((col) => {
                    const mark = col.getShift(date);
                    const cs = markCellStyle(mark);
                    return (
                      <td
                        key={col.key}
                        style={{
                          borderBottom: "1px solid #e2e8f0",
                          borderRight: "1px solid #e2e8f0",
                          padding: 0,
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                      >
                        <div
                          style={{
                            background: cs.background,
                            color: cs.color,
                            fontWeight: mark ? 700 : 400,
                            padding: "4px 6px",
                            minHeight: 22,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: col.isMe ? "2px solid transparent" : "none",
                          }}
                        >
                          {mark || "—"}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p
        style={{
          fontSize: "0.7rem",
          color: "#94a3b8",
          marginTop: 8,
          marginBottom: 0,
        }}
      >
        行をタップすると、その日の詳細を確認できます。緑色＝全員が休みの日（予定が合わせやすい日）。
      </p>
    </div>
  );
};

// =============================================================================
// スタイル
// =============================================================================
const styles = {
  page: { minHeight: "100vh", background: "#f1f5f9" },
  container: {
    maxWidth: "1280px",
    margin: "0 auto",
    padding: "1.5rem 1rem",
    fontFamily: "'Noto Sans JP', sans-serif",
  },
  monthBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "white",
    padding: "12px 16px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    marginBottom: "16px",
  },
  navBtn: {
    background: "transparent",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
    color: "#475569",
  },
  todayBtn: {
    background: "#f1f5f9",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "6px 14px",
    cursor: "pointer",
    color: "#1f2937",
    fontWeight: 600,
    fontSize: "0.85rem",
  },
  monthTitle: { margin: 0, fontSize: "1.3rem", fontWeight: 700, color: "#0f172a", minWidth: 140 },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    background: "linear-gradient(135deg, #0891b2, #0e7490)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  badge: {
    background: "rgba(255,255,255,0.3)",
    padding: "2px 8px",
    borderRadius: "10px",
    fontSize: "0.75rem",
  },
  bodyGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 360px",
    gap: "16px",
  },
  calendarCard: {
    background: "white",
    borderRadius: "12px",
    padding: "16px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  },
  weekHeader: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 4,
    marginBottom: 4,
  },
  weekCell: {
    textAlign: "center",
    padding: "8px 0",
    fontWeight: 700,
    fontSize: "0.85rem",
  },
  dayGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 4,
  },
  dayCell: {
    minHeight: 90,
    padding: "6px",
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    transition: "transform 0.1s, box-shadow 0.1s",
  },
  dayCellEmpty: { minHeight: 90 },
  dayNum: { fontSize: "1rem", fontWeight: 700, textAlign: "left" },
  shiftMark: {
    marginTop: 6,
    fontSize: "1rem",
    fontWeight: 700,
    color: "#0891b2",
    textAlign: "center",
    background: "#ecfeff",
    borderRadius: "4px",
    padding: "2px 0",
  },
  eventDots: {
    display: "flex",
    gap: 3,
    justifyContent: "center",
    marginTop: "auto",
    paddingTop: 4,
  },
  eventDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
  },
  detailCard: {
    background: "white",
    borderRadius: "12px",
    padding: "16px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    height: "fit-content",
    position: "sticky",
    top: "1rem",
  },
  detailHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    paddingBottom: 12,
    borderBottom: "1px solid #e5e7eb",
    marginBottom: 12,
  },
  detailTitle: { margin: 0, fontSize: "1.1rem", color: "#0f172a", fontWeight: 700 },
  detailSection: { marginBottom: "1.25rem" },
  sectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "0.4rem",
  },
  myShiftBox: {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "12px",
    textAlign: "center",
  },
  myShiftMark: {
    fontSize: "1.5rem",
    fontWeight: 800,
    color: "#0891b2",
  },
  miniAddBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 8px",
    background: "#ecfeff",
    border: "1px solid #67e8f9",
    color: "#0e7490",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  eventItem: {
    display: "flex",
    alignItems: "center",
    padding: "8px 10px",
    background: "#f8fafc",
    borderRadius: "6px",
    marginBottom: 4,
  },
  iconBtnSmall: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 4,
    fontSize: "0.85rem",
    color: "#64748b",
  },
  connRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 10px",
    background: "#f8fafc",
    borderRadius: "6px",
  },
  connShiftMark: {
    fontWeight: 700,
    color: "#0891b2",
    background: "#ecfeff",
    padding: "2px 10px",
    borderRadius: "4px",
    fontSize: "0.95rem",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: 20,
  },
  modal: {
    background: "white",
    borderRadius: "12px",
    width: "min(700px, 100%)",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  bottomSheet: {
    background: "white",
    borderRadius: "16px 16px 0 0",
    width: "100%",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 -8px 30px rgba(0,0,0,0.25)",
    position: "relative",
  },
  sheetGrabber: {
    width: 40,
    height: 4,
    background: "#cbd5e1",
    borderRadius: 2,
    margin: "10px auto 0",
  },
  sheetCloseBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#64748b",
    padding: 4,
  },
  modalHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: { margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "#0f172a" },
  closeBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#64748b",
    padding: 4,
  },
  modalBody: { padding: "16px 20px", overflowY: "auto", flex: 1 },
  modalFooter: {
    padding: "12px 20px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    marginBottom: "12px",
  },
  searchInput: { flex: 1, border: "none", outline: "none", fontSize: "0.9rem" },
  staffList: { display: "flex", flexDirection: "column", gap: 6 },
  staffRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    transition: "background 0.15s",
  },
  addBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "6px 12px",
    background: "#0891b2",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  removeBtn: {
    padding: "6px 14px",
    background: "white",
    color: "#dc2626",
    border: "1px solid #fca5a5",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  formGroup: { marginBottom: "1rem" },
  label: { display: "block", marginBottom: 4, fontSize: "0.85rem", fontWeight: 600, color: "#475569" },
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    fontSize: "0.95rem",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  primaryBtn: {
    padding: "8px 18px",
    background: "linear-gradient(135deg, #0891b2, #0e7490)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  cancelBtn: {
    padding: "8px 18px",
    background: "white",
    border: "1px solid #cbd5e1",
    color: "#475569",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.9rem",
  },
};

export default CalendarPage;
